import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type {
  DashboardNowResponse,
  DashboardSalesResponse,
} from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney, subtract, toString } from '@aeris/money'
import { Button, cn } from '@aeris/ui'

import { fetchOldManagementAccount } from '../lib/api/exchanges.ts'
import { useAuth } from '../lib/auth-context.ts'

import { SplitBar } from './dashboard-kit.tsx'

/**
 * Vales e trocas, loja × gestão antiga — F219.
 *
 * O dono: *"quanto é de vale deles, quanto de outra loja… tudo principal tá
 * escondido"*. Era uma linha de dica embaixo de um número pequeno. Agora são
 * dois lados, cada um com o que está em aberto, para quantos clientes, o que
 * foi dado e o que foi gasto no período — e, do lado da gestão antiga, o que
 * ela já deve à loja (F216): o vale antigo que virou produto da Elite.
 *
 * "Em aberto" é de agora e da loja inteira (vale é do cliente, não da
 * filial); emitido, usado e trocas são do período escolhido.
 */

const brl = (amount: string): string => formatMoney(parseAmount(amount))

/** O total menos a parte da gestão antiga: o que é da loja (F214). */
const minus = (total: string, old: string): string =>
  toString(subtract(parseAmount(total), parseAmount(old)))

const plural = (count: number, one: string, many: string): string =>
  `${String(count)} ${count === 1 ? one : many}`

const Line = ({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string | undefined
}): ReactNode => (
  <div className="flex items-baseline justify-between gap-3 border-t border-(--border-subtle) py-1.5 text-(length:--text-sm)">
    <span className="text-(--text-secondary)">{label}</span>
    <span className="text-right">
      <span className="font-(--font-mono) tabular-nums text-(--text-primary)">
        {value}
      </span>
      {hint === undefined ? null : (
        <span className="ml-1.5 text-(length:--text-xs) text-(--text-muted)">
          {hint}
        </span>
      )}
    </span>
  </div>
)

const Side = ({
  title,
  tone,
  open,
  customers,
  issued,
  issuedCount,
  used,
  exchanges,
  defect,
  children,
}: {
  title: string
  tone: 'brand' | 'warning'
  open: string
  customers: number
  issued: string
  issuedCount: number
  used: string
  exchanges: number
  defect: number
  children?: ReactNode
}): ReactNode => (
  <section
    aria-label={title}
    className="flex flex-col gap-2 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-raised) p-3"
  >
    <h3 className="flex items-center gap-2 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
      <span
        aria-hidden="true"
        className={cn(
          'h-2.5 w-2.5 rounded-(--radius-full)',
          tone === 'brand' ? 'bg-(--brand-primary)' : 'bg-(--warning)'
        )}
      />
      {title}
    </h3>
    <div className="flex flex-col">
      <span className="text-(length:--text-xs) text-(--text-muted)">
        Em aberto agora
      </span>
      <span className="font-(--font-mono) text-(length:--text-xl) font-(--weight-semibold) tabular-nums text-(--text-primary)">
        {brl(open)}
      </span>
      <span className="text-(length:--text-xs) text-(--text-secondary)">
        {customers === 0
          ? 'nenhum cliente com vale'
          : `para ${plural(customers, 'cliente', 'clientes')}`}
      </span>
    </div>
    <div className="flex flex-col">
      <Line
        label="Vale dado no período"
        value={brl(issued)}
        hint={plural(issuedCount, 'vale', 'vales')}
      />
      <Line label="Vale gasto no período" value={brl(used)} />
      <Line
        label="Trocas no período"
        value={String(exchanges)}
        hint={defect === 0 ? undefined : `${String(defect)} por defeito`}
      />
    </div>
    {children}
  </section>
)

export const VoucherOverview = ({
  exchanges,
  vouchers,
  onOpenExchanges,
}: {
  exchanges: DashboardSalesResponse['exchanges']
  vouchers: DashboardNowResponse['vouchers']
  onOpenExchanges: () => void
}): ReactNode => {
  const { allows } = useAuth()
  const canSeeAccount = allows('report.read')

  // A mesma conta da tela Trocas (F216), e o mesmo cache.
  const account = useQuery({
    queryKey: ['exchanges', 'old-management'],
    queryFn: ({ signal }) => fetchOldManagementAccount(signal),
    enabled: canSeeAccount,
  })

  const storeOpen = minus(vouchers.outstanding, vouchers.outstandingOld)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-(length:--text-sm) text-(--text-secondary)">
            Vale em aberto agora, na loja inteira
          </span>
          <span className="font-(--font-mono) text-(length:--text-xl) font-(--weight-semibold) tabular-nums text-(--text-primary)">
            {brl(vouchers.outstanding)}
            <span className="ml-2 font-(--font-sans) text-(length:--text-xs) font-(--weight-normal) text-(--text-secondary)">
              {`para ${plural(vouchers.customers, 'cliente', 'clientes')}`}
            </span>
          </span>
        </div>
        <SplitBar
          caption="Vale em aberto: loja e gestão antiga"
          parts={[
            { label: 'Loja', amount: storeOpen },
            { label: 'Gestão antiga', amount: vouchers.outstandingOld },
          ]}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Side
          title="Da loja"
          tone="brand"
          open={storeOpen}
          customers={Math.max(vouchers.customers - vouchers.customersOld, 0)}
          issued={minus(
            exchanges.vouchersIssued.amount,
            exchanges.vouchersIssuedOld.amount
          )}
          issuedCount={Math.max(
            exchanges.vouchersIssued.count - exchanges.vouchersIssuedOld.count,
            0
          )}
          used={minus(
            exchanges.vouchersRedeemed,
            exchanges.vouchersRedeemedOld
          )}
          exchanges={Math.max(exchanges.completed - exchanges.completedOld, 0)}
          defect={Math.max(exchanges.defect - exchanges.defectOld, 0)}
        />
        <Side
          title="Da gestão antiga"
          tone="warning"
          open={vouchers.outstandingOld}
          customers={vouchers.customersOld}
          issued={exchanges.vouchersIssuedOld.amount}
          issuedCount={exchanges.vouchersIssuedOld.count}
          used={exchanges.vouchersRedeemedOld}
          exchanges={exchanges.completedOld}
          defect={exchanges.defectOld}
        >
          {canSeeAccount ? (
            <div className="mt-1 flex flex-wrap items-center justify-between gap-2 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2">
              <div className="flex flex-col">
                <span className="text-(length:--text-xs) font-(--weight-medium) text-(--text-primary)">
                  A cobrar da gestão antiga
                </span>
                {account.isError ? (
                  <span className="text-(length:--text-sm) text-(--text-secondary)">
                    Não carregou
                  </span>
                ) : (
                  <span className="font-(--font-mono) text-(length:--text-lg) font-(--weight-semibold) tabular-nums text-(--text-primary)">
                    {account.data === undefined ? '…' : brl(account.data.used)}
                  </span>
                )}
                <span className="text-(length:--text-xs) text-(--text-secondary)">
                  vale antigo que já virou produto da loja, desde o começo
                </span>
              </div>
              <Button variant="secondary" size="sm" onClick={onOpenExchanges}>
                Ver por cliente
              </Button>
            </div>
          ) : null}
        </Side>
      </div>

      <p className="text-(length:--text-xs) text-(--text-muted)">
        {exchanges.rejected === 0
          ? null
          : `${plural(exchanges.rejected, 'troca recusada', 'trocas recusadas')} no período. `}
        Gestão antiga é a troca de peça vendida antes do Aeris (&ldquo;produto
        de fora&rdquo;, sem venda aqui). O vale antigo é o primeiro a ser gasto.
      </p>
    </div>
  )
}
